import { type FieldsetHTMLAttributes } from 'react';

import { cn } from '../lib/cn.js';

/*
 * RadioCardGroup renders a fieldset of card-style radio buttons. Each option
 * wraps a visually hidden <input type="radio"> inside a <label>, so native
 * keyboard navigation (arrow keys) and screen-reader semantics work without
 * any JS involvement.
 *
 * The prototype favours card pickers for setting choices such as team size and
 * use case, where the full set of options fits on screen and a dropdown would
 * obscure context.
 *
 * Usage:
 *   <RadioCardGroup
 *     name="team-size"
 *     value={value}
 *     onChange={setValue}
 *     options={[{ value: 'just_me', label: 'Just me' }, ...]}
 *   />
 */

export interface RadioCardOption {
  value: string;
  label: string;
  description?: string;
}

export interface RadioCardGroupProps
  extends Omit<FieldsetHTMLAttributes<HTMLFieldSetElement>, 'onChange'> {
  name: string;
  value: string;
  onChange: (value: string) => void;
  options: ReadonlyArray<RadioCardOption>;
  orientation?: 'vertical' | 'grid';
  // Column count when orientation is 'grid'. Defaults to 2 to keep the
  // existing grid behaviour (welcome studio setup) unchanged. Tailwind
  // JIT needs literal class strings, so the switch picks from a fixed
  // list rather than interpolating.
  columns?: 2 | 3 | 4;
}

const GRID_COLS_CLASS: Record<NonNullable<RadioCardGroupProps['columns']>, string> = {
  2: 'grid grid-cols-2',
  3: 'grid grid-cols-3',
  4: 'grid grid-cols-4',
};

export function RadioCardGroup({
  className,
  name,
  value,
  onChange,
  options,
  orientation = 'vertical',
  columns = 2,
  disabled,
  ...props
}: RadioCardGroupProps) {
  return (
    <fieldset
      className={cn('border-0 p-0 m-0 group', className)}
      disabled={disabled}
      {...props}
    >
      <div
        className={cn(
          'flex gap-2',
          orientation === 'vertical' ? 'flex-col' : GRID_COLS_CLASS[columns],
        )}
      >
        {options.map((option) => {
          const checked = option.value === value;
          return (
            <label
              key={option.value}
              className={cn(
                /* Card shell */
                'relative flex flex-col gap-0.5 cursor-pointer select-none',
                'rounded-sm border px-4 py-3',
                'transition-colors duration-150',
                /* Unchecked */
                'bg-surface-2 border-border-strong text-text',
                'hover:border-accent/50 hover:bg-surface-3',
                /* Checked */
                checked &&
                  'border-accent bg-accent-bg text-text ring-1 ring-accent',
                /* Disabled - fieldset[disabled] propagates to children natively;
                   we dim the card visually via the group modifier */
                'group-disabled:pointer-events-none group-disabled:opacity-50',
              )}
            >
              {/* Visually hidden radio input - native semantics handle keyboard nav */}
              <input
                type="radio"
                name={name}
                value={option.value}
                checked={checked}
                disabled={disabled}
                onChange={() => onChange(option.value)}
                className="sr-only"
              />
              <span className="text-sm font-sans font-medium leading-tight">
                {option.label}
              </span>
              {option.description !== undefined && (
                <span className="text-xs text-text-muted leading-snug">
                  {option.description}
                </span>
              )}
            </label>
          );
        })}
      </div>
    </fieldset>
  );
}
